import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ShieldCheck } from "lucide-react";
import { staffApi } from "./staffApi";
import { usePinPrompt } from "./PinModal";

const MenuPushNote = () => {
  const [n, setN] = useState(null);
  const [gone, setGone] = useState(false);
  useEffect(() => { staffApi.get("/staff/menu-push-note").then((r) => setN(r.data)).catch(() => {}); }, []);
  if (!n?.found || gone || localStorage.getItem("staff_seen_push") === n.push_id) return null;
  const when = new Date(n.ts).toLocaleString("en-US", { timeZone: "America/Chicago", month: "numeric", day: "numeric", hour: "numeric", minute: "2-digit" });
  const usd = (v) => (isNaN(Number(v)) ? v : `$${Number(v).toFixed(2)}`);
  const line = (r) => r.changes.map((c) =>
    c.field === "price" ? `${usd(c.from)} → ${usd(c.to)}`
      : c.field === "available" ? (c.to === false ? "86'D BY POS" : "BACK ON")
        : c.field === "name" ? `now called “${c.to}”`
          : c.field === "image" ? "new photo"
            : c.field === "category" ? `moved to ${c.to}`
              : "new description").join(" · ");
  return (
    <div data-testid="staff-push-note" className="mt-4 rounded-xl border border-[#D4AF37]/40 bg-[#D4AF37]/10 p-3">
      <div className="flex items-center justify-between gap-2">
        <p className="text-[11px] font-black tracking-widest text-[#D4AF37]">MENU CHANGED BY POS · {when} (Chicago)</p>
        <button data-testid="staff-push-note-dismiss"
          onClick={() => { localStorage.setItem("staff_seen_push", n.push_id); setGone(true); }}
          className="text-[9px] font-bold tracking-widest text-white/50 border border-white/20 rounded-full px-3 py-1 shrink-0">
          GOT IT
        </button>
      </div>
      <div className="mt-1.5 space-y-0.5 text-[12px] font-bold max-h-44 overflow-y-auto">
        {(n.change_rows || []).map((r) => (
          <p key={r.slug} data-testid={`push-note-row-${r.slug}`}>
            <span className="text-[#D4AF37]">{r.name}</span> — {line(r)}
          </p>
        ))}
        {(n.created_names || []).length > 0 && <p><span className="text-green-400">NEW:</span> {n.created_names.join(", ")}</p>}
        {(n.hidden_names || []).length > 0 && <p><span className="text-[#E25822]">OFF THE MENU:</span> {n.hidden_names.join(", ")}</p>}
        {(n.change_rows || []).length === 0 && (n.created_names || []).length === 0 && (n.hidden_names || []).length === 0 &&
          <p className="text-white/50 font-normal">Push matched what we already had — nothing to relay.</p>}
      </div>
    </div>
  );
};

const since = (iso) => new Date(iso).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });

export default function StaffHome({ person, onLogin, onManager, onGoBoard }) {
  const [members, setMembers] = useState(null);
  const [roster, setRoster] = useState([]);
  const [blockers, setBlockers] = useState(null);
  const [askPin, pinEl] = usePinPrompt();

  const load = () => {
    staffApi.get("/staff/members").then((r) => setMembers(r.data.filter((m) => m.active))).catch(() => {});
    staffApi.get("/staff/roster").then((r) => setRoster(r.data.rows || [])).catch(() => {});
  };
  useEffect(() => { load(); const t = setInterval(load, 15000); return () => clearInterval(t); }, []);

  const signIn = async (m, verb) => {
    const pin = await askPin(`${verb} — ${m.name}`, "Enter your PIN");
    if (!pin) return;
    try {
      const r = await staffApi.post("/staff/login", { pin, staff_id: m.staff_id });
      onLogin(r.data);
      toast.success(r.data.shift_started ? `On shift, ${r.data.name} — here's the Board` : `Welcome back, ${r.data.name}`);
      onGoBoard();
    } catch (e) {
      toast.error(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Wrong PIN");
    }
  };

  const endShift = async (m) => {
    const pin = await askPin(`End shift — ${m.name}`, "Your PIN confirms it");
    if (!pin) return;
    try {
      await staffApi.post("/staff/shift/end", { pin });
      toast.success(`Shift ended — thanks, ${m.name}`);
      load();
    } catch (e) {
      const d = e.response?.data?.detail;
      if (e.response?.status === 409 && d?.blockers) setBlockers({ name: m.name, ...d });
      else toast.error(typeof d === "string" ? d : "Could not end shift");
    }
  };

  const managerEntry = async () => {
    const pin = await askPin("Manager access", "Manager or owner PIN");
    if (!pin) return;
    try {
      const r = await staffApi.post("/staff/login", { pin, manage: "1" });
      if (r.data.role !== "manager" && r.data.role !== "owner") { toast.error("Manager PIN required."); return; }
      onManager(r.data);
    } catch { toast.error("Manager PIN required."); }
  };

  if (!members) return <p className="p-8 text-white/50 text-lg">Loading the crew…</p>;
  const onIds = new Set(roster.map((r) => r.staff_id));
  const entry = (id) => roster.find((r) => r.staff_id === id);

  return (
    <div data-testid="staff-home" className="p-4 pb-20 max-w-3xl mx-auto">
      <h1 className="text-2xl font-black tracking-tight mt-2">Who's working?</h1>
      <p className="text-white/45 text-sm font-bold mt-1">Tap your name. Your PIN starts your shift and opens the Board — that's it, no second sign-in.</p>
      <MenuPushNote />
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-5">
        {members.map((m) => {
          const on = onIds.has(m.staff_id);
          return (
            <div key={m.staff_id} data-testid={`staff-tile-${m.staff_id}`}
              className={`s-card rounded-2xl p-5 border-2 ${on ? "border-[#2ECC71]/50" : "border-white/10"}`}>
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-2xl font-black">{m.name}</p>
                  <p className="text-xs text-white/45 font-black uppercase mt-0.5">{m.role === "waiter" ? "VeerGi" : m.role}</p>
                </div>
                <span data-testid={`tile-status-${m.staff_id}`} className={`rounded-full px-3 py-1.5 text-[11px] font-black tracking-wider ${on ? "text-[#2ECC71] bg-[#2ECC71]/15" : "text-white/40 bg-white/5"}`}>
                  {on ? `ON SHIFT · since ${since(entry(m.staff_id).on_since)}` : "OFF SHIFT"}
                </span>
              </div>
              <div className="mt-4 space-y-2">
                {!on ? (
                  <button data-testid={`tile-start-${m.staff_id}`} onClick={() => signIn(m, "Start shift")}
                    className="w-full min-h-[60px] rounded-xl bg-[#D4AF37] text-black font-black text-lg tracking-wide hover:bg-[#F0C745]">
                    START SHIFT
                  </button>
                ) : (
                  <>
                    <button data-testid={`tile-continue-${m.staff_id}`} onClick={() => signIn(m, "Continue")}
                      className="w-full min-h-[60px] rounded-xl bg-[#2ECC71] text-black font-black text-lg tracking-wide hover:opacity-90">
                      CONTINUE TO BOARD
                    </button>
                    <button data-testid={`tile-end-${m.staff_id}`} onClick={() => endShift(m)}
                      className="w-full min-h-[48px] rounded-xl border border-[#F0B429]/50 text-[#F0B429] font-black text-sm tracking-wide">
                      END SHIFT
                    </button>
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>
      <div className="mt-10 flex justify-center">
        <button data-testid="manager-btn" onClick={managerEntry}
          className="flex items-center gap-2 rounded-full border border-white/15 text-white/50 font-black text-xs tracking-[0.25em] px-6 py-3 hover:border-[#D4AF37]/50 hover:text-[#D4AF37]">
          <ShieldCheck size={14} /> MANAGER
        </button>
      </div>
      {pinEl}
      {blockers && (
        <div data-testid="endshift-blockers" className="fixed inset-0 z-[70] bg-black/85 flex items-center justify-center p-4" onClick={() => setBlockers(null)}>
          <div onClick={(e) => e.stopPropagation()} className="w-full max-w-md rounded-3xl bg-[#16161B] border border-[#F0B429]/50 p-5">
            <p className="text-lg font-black text-[#F0B429]">You still have responsibility for:</p>
            <div className="mt-3 space-y-1.5">
              {blockers.blockers.map((b, i) => (
                <p key={i} data-testid={`blocker-${b.order_id}`} className="text-base font-bold text-white/85">• {b.label}</p>
              ))}
            </div>
            <p className="text-sm text-white/50 font-bold mt-3">Pass or complete these orders before ending your shift. Nothing gets orphaned.</p>
            <div className="mt-4 space-y-2">
              <button data-testid="blockers-return-board" onClick={() => { setBlockers(null); onGoBoard(); }}
                className="w-full rounded-xl bg-[#D4AF37] text-black font-black py-3.5 tracking-wide">RETURN TO BOARD</button>
              <button data-testid="blockers-pass-orders" onClick={() => { setBlockers(null); toast("Open each table on the Board and tap PASS TABLE — your colleague confirms with their PIN"); onGoBoard(); }}
                className="w-full rounded-xl border border-white/20 text-white/80 font-black py-3.5 tracking-wide">PASS MY ORDERS</button>
              <button data-testid="blockers-manager-help" onClick={() => { setBlockers(null); toast("Ask a manager — MANAGER tools can transfer your tables or end your shift with everything released"); }}
                className="w-full rounded-xl border border-white/20 text-white/60 font-black py-3.5 tracking-wide">MANAGER ASSISTANCE</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
